import { Injectable, InjectionToken, OnDestroy, inject, signal } from "@angular/core";
import { Observable, Subject, Subscription } from "rxjs";
import { Config } from "src/app/config";
import { AuthService } from "./auth.service";

export interface Nudge {
  type: string;
  orderId?: number;
  // SHILA_PO: the orders and Shila branches the on-prem box just reported on
  orderIds?: number[];
  shilaClinicCodes?: string[];
  // RX_PDF: the RX whose PDF the server made once Meimad's number arrived
  formId?: number;
  state?: string;
  takerName?: string;
}

// Injectable so tests can swap in a fake socket.
export const WEB_SOCKET_FACTORY = new InjectionToken<(url: string, protocols: string[]) => WebSocket>(
  "WEB_SOCKET_FACTORY",
  { providedIn: "root", factory: () => (url, protocols) => new WebSocket(url, protocols) }
);

const FIRST_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
// each reconnect wait is spread +-20 %, so tabs that dropped together do not return together
const BACKOFF_JITTER = 0.2;
const DEFAULT_TIMEOUT_MS = 300000;
// how long a channel replaced on a token refresh still shows as live without its replacement
const LIVE_GRACE_MS = 10000;

const base64url = (s: string) => btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

// The Cognito sub in an ID token, or null when the token is not a readable JWT.
function subOf(idToken: string): string | null {
  try {
    const payload = idToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const sub = JSON.parse(atob(payload))?.sub;
    return typeof sub === "string" && sub ? sub : null;
  } catch {
    return null;
  }
}

// One live AppSync Events connection per tab, on the user's own channel /users/<sub>.
@Injectable({ providedIn: "root" })
export class RealtimeService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly socketFactory = inject(WEB_SOCKET_FACTORY);

  private readonly nudges = new Subject<Nudge>();
  private readonly connected = new Subject<void>();
  readonly nudges$: Observable<Nudge> = this.nudges.asObservable();
  readonly connected$: Observable<void> = this.connected.asObservable();
  readonly live = signal(false);

  private socket: WebSocket | null = null;
  private token = "";
  private wanted = false;
  private subId = "";
  // this socket's subscription is confirmed (live() can also be a replaced channel's grace)
  private subscribed = false;
  private graceTimer: any = null;
  private backoffMs = FIRST_BACKOFF_MS;
  private retryTimer: any = null;
  private watchdog: any = null;
  private timeoutMs = DEFAULT_TIMEOUT_MS;
  private readonly subscriptions = new Subscription();

  constructor() {
    this.subscriptions.add(this.auth.signedIn$.subscribe((t) => this.connect(t)));
    // A refresh moves a wanted connection to the new token but never starts one:
    // one that lands after sign-out began, or the one straight after a session
    // restore (the restore announces itself on signedIn$), changes nothing.
    this.subscriptions.add(
      this.auth.tokenRefreshed$.subscribe((t) => {
        if (this.wanted) this.connect(t);
      })
    );
    this.subscriptions.add(this.auth.signingOut$.subscribe(() => this.disconnect()));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.disconnect();
  }

  connect(idToken: string): void {
    if (!this.hosts() || !idToken) return;
    this.token = idToken;
    this.wanted = true;
    this.backoffMs = FIRST_BACKOFF_MS;
    // a live channel replaced on a token refresh does not flash "live updates off"
    this.open(this.live());
  }

  disconnect(): void {
    this.wanted = false;
    if (this.socket && this.subscribed) this.send(this.socket, { type: "unsubscribe", id: this.subId });
    this.drop();
  }

  // Both realtime hosts are set in this build's config: the feature is on.
  configured(): boolean {
    return !!this.hosts();
  }

  private hosts(): { http: string; ws: string } | null {
    const http = Config.assetConfigData?.["realtimeHttpHost"];
    const ws = Config.assetConfigData?.["realtimeWsHost"];
    return http && ws ? { http, ws } : null;
  }

  private authorization() {
    return { Authorization: this.token, host: this.hosts()!.http };
  }

  private open(keepLive = false) {
    this.drop(keepLive);
    const hosts = this.hosts();
    if (!hosts) return;
    const header = base64url(JSON.stringify({ host: hosts.http, Authorization: this.token }));
    const ws = this.socketFactory("wss://" + hosts.ws + "/event/realtime", [
      "aws-appsync-event-ws",
      "header-" + header,
    ]);
    this.socket = ws;
    this.timeoutMs = DEFAULT_TIMEOUT_MS;
    this.armWatchdog(ws);
    ws.onopen = () => this.send(ws, { type: "connection_init" });
    ws.onmessage = (e) => this.onMessage(ws, e.data);
    ws.onclose = () => this.onLost(ws);
    ws.onerror = () => this.onLost(ws);
  }

  private onMessage(ws: WebSocket, raw: string) {
    if (ws !== this.socket) return;
    let m: any;
    try {
      m = JSON.parse(raw);
    } catch {
      return;
    }
    switch (m?.type) {
      case "connection_ack": {
        const sub = subOf(this.token);
        if (!sub) {
          // no channel to ask for; retry with backoff (a refresh brings a new token)
          this.onLost(ws);
          return;
        }
        this.timeoutMs = m.connectionTimeoutMs || DEFAULT_TIMEOUT_MS;
        this.armWatchdog(ws);
        this.subId = "sub-" + Math.random().toString(36).slice(2, 12);
        this.send(ws, {
          type: "subscribe",
          id: this.subId,
          channel: "/users/" + sub,
          authorization: this.authorization(),
        });
        break;
      }
      case "ka":
        this.armWatchdog(ws);
        break;
      case "subscribe_success":
        this.backoffMs = FIRST_BACKOFF_MS;
        this.subscribed = true;
        clearTimeout(this.graceTimer);
        this.graceTimer = null;
        this.live.set(true);
        this.connected.next();
        break;
      case "data":
        this.deliver(m.event);
        break;
      case "subscribe_error":
      case "error":
        this.onLost(ws);
        break;
    }
  }

  private deliver(event: any) {
    for (const e of Array.isArray(event) ? event : [event]) {
      let n: any;
      try {
        n = typeof e === "string" ? JSON.parse(e) : e;
      } catch {
        continue; // an unparseable event is not ours
      }
      // a nudge is an object; null, numbers, strings and arrays are not ours either
      if (n && typeof n === "object" && !Array.isArray(n)) this.nudges.next(n);
    }
  }

  private onLost(ws: WebSocket) {
    if (ws !== this.socket) return;
    this.drop();
    if (!this.wanted) return;
    const wait = Math.round(this.backoffMs * (1 - BACKOFF_JITTER + Math.random() * 2 * BACKOFF_JITTER));
    this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.wanted) this.open();
    }, wait);
  }

  private armWatchdog(ws: WebSocket) {
    clearTimeout(this.watchdog);
    this.watchdog = setTimeout(() => this.onLost(ws), this.timeoutMs);
  }

  // Closes whatever is open and clears every timer; the old socket can no longer call back.
  // keepLive: the channel is being replaced, so live() holds until the replacement
  // subscribes, fails (a drop without keepLive), or LIVE_GRACE_MS passes.
  private drop(keepLive = false) {
    clearTimeout(this.watchdog);
    clearTimeout(this.retryTimer);
    this.watchdog = this.retryTimer = null;
    const ws = this.socket;
    this.socket = null;
    this.subscribed = false;
    if (keepLive) {
      this.graceTimer ??= setTimeout(() => {
        this.graceTimer = null;
        this.live.set(false);
      }, LIVE_GRACE_MS);
    } else {
      clearTimeout(this.graceTimer);
      this.graceTimer = null;
      this.live.set(false);
    }
    if (ws) {
      try {
        ws.close();
      } catch {
        // already closed
      }
    }
  }

  private send(ws: WebSocket, message: object) {
    try {
      ws.send(JSON.stringify(message));
    } catch {
      // socket not open; the close handler reconnects
    }
  }
}
